import React from 'react';
import { Card, Typography, Grid, Box } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const NetworkOverview = ({ data }) => {
  if (!data || !data.network_info) return null;

  const chartData = data.network_info.map((item) => [
    { name: 'Active', value: item.active, color: COLORS[0] },
    { name: 'Inactive', value: item.total - item.active, color: '#E0E0E0' },
  ]);

  return (
    <Card sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
      <Grid
        container
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 4 }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: '600' }}>
          {data.section_header_display_title || 'Network Overview'}
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: '500' }}>
          {data.month_and_year}
        </Typography>
      </Grid>

      <Grid
        container
        spacing={2}
        rowSpacing={3}
      >
        {!data || !data.network_info || data.network_info.length === 0 ? (
          <Grid
            container
            justifyContent='center'
            alignItems='center'
            sx={{ height: '100%', minHeight: 200 }}
          >
            <Box textAlign='center'>
              <ErrorOutline sx={{ fontSize: 60, color: '#B2B5C4' }} />
              <Typography
                sx={{ fontSize: 16, fontWeight: 600, color: '#8E92A8', mt: 1 }}
              >
                No Data Found
              </Typography>
            </Box>
          </Grid>
        ) : (
          data.network_info.map((item, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={index}
            >
              <Grid
                container
                alignItems='center'
                spacing={2}
              >
                <Grid
                  item
                  xs={6}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <ResponsiveContainer
                    width={'100%'}
                    height={100}
                  >
                    <PieChart>
                      <Pie
                        data={chartData[index]}
                        dataKey='value'
                        cx='50%'
                        cy='100%'
                        startAngle={180}
                        endAngle={0}
                        outerRadius={80}
                        innerRadius={65}
                        labelLine={false}
                        cornerRadius={6}
                      >
                        {chartData[index].map((entry, idx) => (
                          <Cell
                            key={`cell-${idx}`}
                            fill={entry.color}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Centered Text */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '80%',
                      left: '52%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    {item.active} / {item.total}
                    <Typography
                      variant='body2'
                      sx={{ color: '#666' }}
                    >
                      {item.display_title}
                    </Typography>
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={6}
                  sx={{
                    boxShadow: 1,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: '600' }}>
                    {item.display_title}
                  </Typography>

                  {/* Active & Inactive Data */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='body2'>
                      <strong>Active:</strong> {item.active}{' '}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>Inactive:</strong> {item.total - item.active}
                    </Typography>
                  </Box>

                  {/* Footer Section with Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item?.section_footer?.type === 'INCREASED' ? (
                      <KeyboardDoubleArrowUp sx={{ color: '#4CAF50' }} />
                    ) : (
                      <KeyboardDoubleArrowDown sx={{ color: '#D32F2F' }} />
                    )}
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 'bold',
                        color:
                          item?.section_footer?.type === 'INCREASED'
                            ? '#2E7D32'
                            : '#C62828',
                      }}
                    >
                      {item.section_footer.label}: {item.section_footer.value}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          ))
        )}
      </Grid>
    </Card>
  );
};

export default NetworkOverview;
